import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Navbar } from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { 
  CheckCircle, Send, Loader2, AlertTriangle, Lightbulb, HelpCircle
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PARCOURS_LIST = [
  { id: 'ao-publique', title: 'Répondre à un AO publique' },
  { id: 'achats', title: 'Achats' },
  { id: 'maintenance', title: 'Maintenance' },
  { id: 'chantiers', title: 'Chantiers' },
  { id: 'ocr', title: 'OCR' },
  { id: 'relance-impayes', title: 'Relance impayés' },
  { id: 'comptabilite', title: 'Comptabilité' },
  { id: 'marche-bcd', title: 'Marché BCD' },
  { id: 'marketing', title: 'Marketing (MC Growth / Data Cloud)' },
];

const ETAPES = [
  'Étape 1',
  'Étape 2',
  'Étape 3',
  'Quiz',
  'Autre'
];

export const SuggestionsPage = () => {
  const { user, logEvent } = useAuth();
  
  const [formData, setFormData] = useState({
    type: '',
    parcours: '',
    etape: '',
    description: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.type || !formData.parcours || !formData.etape || !formData.description) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }

    setSubmitting(true);
    
    try {
      await axios.post(`${API}/feedback/suggestion`, {
        user_id: user.id,
        ...formData
      });
      
      logEvent('suggestion_sent', formData.parcours, formData.etape, { type: formData.type });
      toast.success('Votre signalement a été envoyé !');
      setSubmitted(true);
    } catch (error) {
      console.error('Error submitting suggestion:', error);
      toast.error('Erreur lors de l\'envoi. Veuillez réessayer.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReset = () => {
    setFormData({
      type: '',
      parcours: '',
      etape: '',
      description: ''
    });
    setSubmitted(false);
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'amelioration': return <Lightbulb className="h-5 w-5 text-[#ff9a52]" />;
      case 'bug': return <AlertTriangle className="h-5 w-5 text-red-500" />;
      case 'incomprehension': return <HelpCircle className="h-5 w-5 text-blue-500" />;
      default: return null;
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#f2f2f2]" data-testid="suggestion-success">
        <Navbar />
        <div className="container mx-auto px-4 md:px-8 py-12 max-w-2xl">
          <Card className="text-center">
            <CardContent className="py-12">
              <div className="h-20 w-20 rounded-full bg-green-100 mx-auto mb-6 flex items-center justify-center">
                <CheckCircle className="h-10 w-10 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-[#571925] mb-2 font-['Outfit']">
                Signalement envoyé !
              </h2>
              <p className="text-gray-600 mb-8">
                Merci de contribuer à l'amélioration de la plateforme.
              </p>
              <Button 
                onClick={handleReset}
                className="bg-[#ff9a52] hover:bg-[#e08540] text-white rounded-full"
              >
                Faire un autre signalement
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f2f2f2]" data-testid="suggestions-page">
      <Navbar />
      
      <div className="container mx-auto px-4 md:px-8 py-8 max-w-2xl">
        <Card>
          <CardHeader>
            <CardTitle className="text-2xl text-[#571925] font-['Outfit'] flex items-center gap-2">
              <AlertTriangle className="h-6 w-6" />
              Suggestions / Incidents
            </CardTitle>
            <CardDescription>
              Signalez un problème ou proposez une amélioration
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Type */}
              <div className="space-y-2">
                <Label className="text-base font-semibold text-[#1a1a1a]">
                  Type de signalement *
                </Label>
                <div className="grid grid-cols-3 gap-4">
                  {[
                    { value: 'amelioration', label: 'Amélioration', icon: Lightbulb, color: 'text-[#ff9a52]' },
                    { value: 'bug', label: 'Bug', icon: AlertTriangle, color: 'text-red-500' },
                    { value: 'incomprehension', label: 'Incompréhension', icon: HelpCircle, color: 'text-blue-500' }
                  ].map(({ value, label, icon: Icon, color }) => (
                    <button
                      key={value}
                      type="button"
                      onClick={() => setFormData({...formData, type: value})}
                      className={`p-4 rounded-lg border-2 transition-all text-center ${
                        formData.type === value 
                          ? 'border-[#ff9a52] bg-[#ff9a52]/10' 
                          : 'border-gray-200 hover:border-[#cc5759]'
                      }`}
                      data-testid={`type-${value}`}
                    >
                      <Icon className={`h-6 w-6 mx-auto mb-2 ${color}`} />
                      <span className="text-sm font-medium">{label}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Parcours */}
              <div className="space-y-2">
                <Label className="text-base font-semibold text-[#1a1a1a]">
                  Parcours concerné *
                </Label>
                <Select 
                  value={formData.parcours} 
                  onValueChange={(value) => setFormData({...formData, parcours: value})}
                >
                  <SelectTrigger data-testid="parcours-select">
                    <SelectValue placeholder="Sélectionnez un parcours" />
                  </SelectTrigger>
                  <SelectContent>
                    {PARCOURS_LIST.map((p) => (
                      <SelectItem key={p.id} value={p.id}>{p.title}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Étape */}
              <div className="space-y-2">
                <Label className="text-base font-semibold text-[#1a1a1a]">
                  Étape concernée *
                </Label>
                <Select 
                  value={formData.etape} 
                  onValueChange={(value) => setFormData({...formData, etape: value})}
                >
                  <SelectTrigger data-testid="etape-select">
                    <SelectValue placeholder="Sélectionnez une étape" />
                  </SelectTrigger>
                  <SelectContent>
                    {ETAPES.map((e) => (
                      <SelectItem key={e} value={e}>{e}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Description */}
              <div className="space-y-2">
                <Label className="text-base font-semibold text-[#1a1a1a]">
                  Description *
                </Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  placeholder="Décrivez le problème ou votre suggestion en détail..."
                  className="min-h-[150px] resize-none"
                  data-testid="description-input"
                />
              </div>

              {/* Submit */}
              <Button 
                type="submit"
                disabled={submitting}
                className="w-full bg-[#ff9a52] hover:bg-[#e08540] text-white rounded-full h-12 font-semibold"
                data-testid="submit-suggestion-btn"
              >
                {submitting ? (
                  <>
                    <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                    Envoi en cours...
                  </>
                ) : (
                  <>
                    <Send className="h-5 w-5 mr-2" />
                    Envoyer le signalement
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
